import { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';

export default function Missions({ userId, profile, setProfile }) {
  const [missions, setMissions]         = useState([]);
  const [sideQuest, setSideQuest]       = useState(null);
  const [questData, setQuestData]       = useState(null);
  const [generating, setGenerating]     = useState(false);
  const [genQuest, setGenQuest]         = useState(false);
  const [photo, setPhoto]               = useState(null);
  const [photoB64, setPhotoB64]         = useState(null);
  const [verifying, setVerifying]       = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [tab, setTab]                   = useState('mission');
  const fileRef = useRef(null);

  useEffect(() => {
    api.getMissions(userId).then(r => setMissions(r.data));
    api.getQuests(userId).then(r => {
      const today = new Date().toISOString().slice(0, 10);
      const todayQuest = r.data.find(q => q.questDate === today);
      if (todayQuest) setSideQuest(todayQuest);
    });
  }, [userId]);

  async function generateMission() {
    setGenerating(true);
    try {
      const res = await api.generateMission(userId);
      setMissions(m => [res.data, ...m]);
    } catch (e) {
      const msg = e.response?.data?.error || 'Errore';
      alert(msg);
    } finally {
      setGenerating(false);
    }
  }

  async function generateQuest() {
    setGenQuest(true);
    try {
      const res = await api.generateQuest(userId);
      setSideQuest(res.data.quest);
      try { setQuestData(JSON.parse(res.data.questData)); }
      catch { setQuestData(null); }
    } catch (e) {
      alert(e.response?.data?.error || 'Errore');
    } finally {
      setGenQuest(false);
    }
  }

  async function completeMission(id) {
    const res = await api.completeMission(id, userId);
    setProfile(res.data);
    setMissions(m => m.map(x => x.id === id ? { ...x, completed: true } : x));
  }

	function handlePhoto(e) {
	  const file = e.target.files[0];
	  if (!file) return;
	  setPhoto(URL.createObjectURL(file));

	  const img = new Image();
	  img.onload = () => {
		const MAX = 1024;
		let w = img.width, h = img.height;
		if (w > h && w > MAX) { h = Math.round(h * MAX / w); w = MAX; }
		else if (h > MAX) { w = Math.round(w * MAX / h); h = MAX; }

		const canvas = document.createElement('canvas');
		canvas.width = w; canvas.height = h;
		canvas.getContext('2d').drawImage(img, 0, 0, w, h);
		const compressed = canvas.toDataURL('image/jpeg', 0.8);
		setPhotoB64(compressed.split(',')[1]);
	  };
	  img.src = URL.createObjectURL(file);
	}

  async function verifyPhoto() {
    if (!photoB64 || !sideQuest) return;
    setVerifying(true);
    try {
      const res = await api.verifyQuest({
        userId,
        questId: String(sideQuest.id),
        photoBase64: photoB64,
        mimeType: 'image/jpeg'
      });
      setVerifyResult(res.data);
      if (res.data.verified && res.data.profile) {
        setProfile(res.data.profile);
        setSideQuest(q => ({ ...q, verified: true }));
      }
    } catch {
      setVerifyResult({ verified: false, feedback: 'Errore nella verifica. Riprova.' });
    } finally {
      setVerifying(false);
    }
  }

  function parseMission(desc) {
    try { return JSON.parse(desc); }
    catch { return { titolo: 'Missione', sfide: [], ricompensa: '' }; }
  }

  return (
    <div style={{ paddingTop: 8 }}>

      {/* Tab switcher */}
      <div style={{ display: 'flex', margin: '0 12px 12px', gap: 8 }}>
        {[['mission','🔥 Missione'], ['quest','🌿 Side Quest']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            flex: 1, padding: '10px', borderRadius: 10, border: 'none', cursor: 'pointer',
            background: tab === k ? 'var(--accent)' : 'var(--card)',
            color: tab === k ? '#fff' : 'var(--muted)',
            fontWeight: 700, fontSize: 13
          }}>{l}</button>
        ))}
      </div>

      {/* ── MISSIONE GIORNALIERA ── */}
      {tab === 'mission' && (
        <>
          <div className="card">
            <div className="card-title">boss fight giornaliero</div>
            <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>
              Una missione al giorno — sfide reali per staccarti dal telefono.
            </p>
            <button className="btn btn-primary" onClick={generateMission} disabled={generating}>
              {generating ? '⚡ Generando...' : '⚔️ Genera missione di oggi (+200 XP)'}
            </button>
          </div>

          {missions.map(m => {
            const parsed = parseMission(m.description);
            return (
              <div key={m.id} className={`mission-card ${m.completed ? 'mission-done' : ''}`}>
                <div className="boss-name">{m.completed ? '✅' : '💀'} {parsed.titolo || 'Missione'}</div>
                {parsed.sfide?.map((s, i) => (
                  <div key={i} className="mission-desc">• {s}</div>
                ))}
                {parsed.ricompensa && (
                  <div style={{ fontSize: 12, color: 'var(--accent2)', margin: '8px 0' }}>
                    🏆 {parsed.ricompensa}
                  </div>
                )}
                {!m.completed && (
                  <button className="btn btn-xp" style={{ marginTop: 8, fontSize: 13 }}
                          onClick={() => completeMission(m.id)}>
                    Missione completata! (+200 XP)
                  </button>
                )}
              </div>
            );
          })}
        </>
      )}

      {/* ── SIDE QUEST ── */}
      {tab === 'quest' && (
        <>
          {!sideQuest ? (
            <div className="card">
              <div className="card-title">side quest del mondo reale</div>
              <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>
                Esplora il mondo reale. Fotografa oggetti e natura. Guadagna badge unici e XP.
              </p>
              <button className="btn btn-quest" onClick={generateQuest} disabled={genQuest}>
                {genQuest ? '🌿 Generando quest...' : '🌿 Genera side quest (+150 XP)'}
              </button>
            </div>
          ) : (
            <div className="mission-card quest-card">
              <div className="quest-title">
                {sideQuest.verified ? '✅' : '📸'} {questData?.titolo || 'Side Quest'}
              </div>
              <div className="quest-object">
                📍 Trova e fotografa: <strong style={{ color: 'var(--xp)' }}>
                  {questData?.oggetto_da_fotografare || sideQuest.description}
                </strong>
              </div>
              {questData?.fatto_sorprendente && (
                <div className="quest-fact">💡 {questData.fatto_sorprendente}</div>
              )}

              {!sideQuest.verified && !verifyResult?.verified && (
                <>
                  <div className="photo-upload-area" onClick={() => fileRef.current?.click()}>
                    {photo
                      ? <img src={photo} alt="preview" className="photo-preview" />
                      : <div style={{ color: 'var(--muted)', fontSize: 13 }}>
                          📷 Tocca per scattare o scegliere una foto<br/>
                          <small>Solo oggetti — niente persone</small>
                        </div>
                    }
                  </div>
                  <input ref={fileRef} type="file" accept="image/*"
                         capture="environment" style={{ display: 'none' }}
                         onChange={handlePhoto} />
                  {photo && (
                    <button className="btn btn-quest" onClick={verifyPhoto} disabled={verifying}>
                      {verifying ? '🔍 Verifica in corso...' : '🔍 Verifica con AI (+150 XP)'}
                    </button>
                  )}
                </>
              )}

              {verifyResult && (
                <div style={{
                  marginTop: 12, padding: 12, borderRadius: 10,
                  background: verifyResult.verified ? 'rgba(0,230,118,.1)' : 'rgba(255,69,0,.1)',
                  border: `1px solid ${verifyResult.verified ? 'var(--green)' : 'var(--accent)'}`,
                  fontSize: 13
                }}>
                  {verifyResult.verified
                    ? `🎉 ${verifyResult.feedback} — +${verifyResult.xpGained} XP${verifyResult.badge ? ` · Badge: ${verifyResult.badge}` : ''}`
                    : `❌ ${verifyResult.feedback}`
                  }
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
